import {
  DropdownButtonType,
  List,
  ListItem,
  SingleSelectDropdown,
  SingleSelectDropdownProps  } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { TypeUnsafeTranslation } from '~src/commonInfra/services/IReactTranslateService';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { SiteRoleInfoLink } from '~src/userManagement/components/SiteRoleInfoLink';

import {
  CreatorTier,
  ExplorerTier,
  LicensingTier,
  LicensingTierLocPrefix,
  SiteRoleOption,
  SiteRoleOptionsResource,
  UnlicensedTier,
  ViewerTier
  } from '../../services/resources/SiteRoleOptions';
import { SiteRolesResource } from '../../services/resources/SiteRoles';

const SITE_ROLE_PICKER_TEST_ID = 'site-role-picker';

export const DefaultDropdownMenuWidth = 370;
export const SiteRoleInfoContainerStyle: React.CSSProperties = {
  display: 'inline-flex'
};
const TierSections = [CreatorTier, ExplorerTier, ViewerTier, UnlicensedTier];
const SectionDivider = {separator: true};
const HeaderTestIdSuffix = 'Header';

/**
 * A drop down menu that groups site roles based on subscription licensing tiers
 */
export class SiteRolePickerMenu extends React.PureComponent<SiteRolePickerMenu.Props> {
  private defaultSiteRoleDropdownWidth = DefaultDropdownMenuWidth - CommonStyles.Sizes.DefaultSpace - (2 * CommonStyles.Sizes.TextBoxBorderSize);
  public boundSelectSiteRole: (value: string) => void = value => this.handleSelection(value);

  protected handleSelection(newOption: string): void {
    const selectedRoleOption: SiteRoleOption = _.find(this.props.siteRoleOptions, (option: SiteRoleOption) => {
        return newOption === option.role;
    });
    this.props.onOptionSelected(selectedRoleOption);
  }

  public render(): JSX.Element {
    const dropdownWidth = (this.props.style && this.props.style.width) ? this.props.style.width : this.defaultSiteRoleDropdownWidth;

    return (<div className={style({
      display: 'inline-block'
    })}>
      <SingleSelectDropdown
      className={style({
        width: dropdownWidth,
        ...this.props.style,
      })}
      disabled={this.props.isDisabled}
      value={this.getSelectedValue()}
      onChange={this.boundSelectSiteRole}
      testId={SITE_ROLE_PICKER_TEST_ID}
      list={this.getMenuSiteRoleInfo()}
      label={this.props.dropdownLabel}
      {...(!this.props.dropdownLabel && { 'aria-label': `${this.props.siteName} ${Messages.siteRole_colon()} ${this.getSiteRoleOptionDisplayValue(this.props.selectedRole)}` })}
      type={DropdownButtonType.Outline}
      tabIndex={this.props.tabIndex}
    />{this.props.showSiteRoleInfoLink && this.getDropdownLabelWithInfo()}</div>);
  }

  private getDropdownLabelWithInfo(): JSX.Element {
    return <div style={SiteRoleInfoContainerStyle}>
      <SiteRoleInfoLink/>
      {this.props.message}
    </div>;
  }

  protected getMenuSiteRoleInfo(): JSX.Element {
    let siteRoleOptions: ListItem[] = [];
    if (this.props.includeAnySiteRoleOption) {
      siteRoleOptions = siteRoleOptions.concat({
        value: SiteRoleOptionsResource.instance.getAnySiteRole().role,
        content: this.getSiteRoleOptionDisplayValue(SiteRoleOptionsResource.instance.getAnySiteRole()),
        disabled: false
      });
      siteRoleOptions = siteRoleOptions.concat(SectionDivider);
    }
    for (let i = 0; i < TierSections.length; i++) {
      const tier = TierSections[i];
      const siteRoleOptionsForTier = this.getSiteRoleOptionsForTier(tier);
      if (siteRoleOptionsForTier.length > 0 && tier !== UnlicensedTier){
        const tierSectionHeader = this.getHeaderForTierSection(tier);
        siteRoleOptions = siteRoleOptions.concat(tierSectionHeader);
      }
      siteRoleOptions = siteRoleOptions.concat(siteRoleOptionsForTier);
      if (i < TierSections.length-1 && siteRoleOptionsForTier.length > 0) {
        siteRoleOptions = siteRoleOptions.concat(SectionDivider);
      }
    }
    const listStyle = this.props.style?.width
      ? { width: this.props.style.width, minWidth: this.props.style.width, maxWidth: this.props.style.width }
      : undefined;
    return <List items={siteRoleOptions} style={listStyle} />;
  }

  private getHeaderForTierSection(tierRole: LicensingTier): ListItem {
    return {
      value: tierRole + HeaderTestIdSuffix,
      content: TypeUnsafeTranslation.translate(LicensingTierLocPrefix + tierRole),
      group: true,
    };
  }

  private getSiteRoleOptionsForTier(tierRole: LicensingTier): ListItem[]{
    const siteRoleOptionsForTier = _.filter(this.props.siteRoleOptions, (siteRoleOption: SiteRoleOption) => {
      return siteRoleOption.licenseTierRole === tierRole;
    });
    return siteRoleOptionsForTier.map((siteRoleOption: SiteRoleOption) => {
        return {
          value: siteRoleOption.role,
          content: this.getSiteRoleOptionDisplayValue(siteRoleOption),
          disabled: this.checkSiteRoleDisabled(siteRoleOption)
        };
    });
  }

  private getSiteRoleOptionDisplayValue(siteRoleOption: SiteRoleOption) {
    const siteRoleDisplayValue = TypeUnsafeTranslation.translate(siteRoleOption.locKey);
    return siteRoleDisplayValue;
  }

  private checkSiteRoleDisabled(siteRoleOption: SiteRoleOption){
      if(siteRoleOption.disabled){
        return true;
      }
      if(this.props.disabledRoleOptions){
        for (let i = 0; i < this.props.disabledRoleOptions.length; i++) {
          if (this.props.disabledRoleOptions[i].licenseTierRole === siteRoleOption.licenseTierRole &&
              this.props.disabledRoleOptions[i].locKey === siteRoleOption.locKey &&
              this.props.disabledRoleOptions[i].role === siteRoleOption.role) {
              return true;
          }
        }
      }
      return false;
  }

  private getSelectedValue(): string{
    const siteRoleOption = this.props.selectedRole;
    if(siteRoleOption !== undefined && siteRoleOption.role === SiteRolesResource.Mixed){
        return TypeUnsafeTranslation.translate(siteRoleOption.locKey);
    }
    return siteRoleOption.role;
  }
}

export namespace SiteRolePickerMenu {

  export interface Props extends Omit<SingleSelectDropdownProps, 'list' | 'type' | 'label'> {
    getSiteRoleOptionBySiteRole: (siteRole: string) => SiteRoleOption;
    onOptionSelected: (siteRoleOption: SiteRoleOption) => void;
    selectedRole: SiteRoleOption;
    siteRoleOptions: SiteRoleOption[];
    disabledRoleOptions?: SiteRoleOption[];
    dropdownLabel?: string;
    isDisabled?: boolean;
    panelStyle?: React.CSSProperties;
    style?: React.CSSProperties;
    showSiteRoleInfoLink?: boolean;
    includeAnySiteRoleOption?: boolean;
    tabIndex?: -1;
    siteName?: string;
  }

}
